'use client';
import { useCallback } from 'react';
import { Box, useMediaQuery } from '@mui/material';
import { NavbarHeader } from 'src/components/flow';
import ProgressBar from 'src/components/progress-bar';
import ProgressBarV2 from 'src/components/progress-bar-v2';
import { PrimaryGoal } from 'src/types/inputs';
import { v4 as uuid } from 'uuid';
import { Screen } from 'src/lib/flows/screens';
import {
  getProgressValues,
  getExitModalRedirectLink,
} from 'src/lib/quiz-utils';
import { getActiveFlow } from 'src/redux/actions/flow';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import {
  useCallbackEvent,
  useSegmentPageAction,
} from 'src/utils/analytics/hooks';
import {
  EntityName,
  EventType,
  FormName,
  PageActionName,
  PageActionType,
} from 'src/utils/analytics/constants';
import {
  getInputs,
  getPageNumber,
  getCurrentStep,
  getInitialScreen,
  getExperiments,
} from 'src/redux/selectors';
import {
  isNerdyTipAndProgressBarActive,
  isProgressBarOnlyActive,
} from 'src/lib/experiments';
import { theme } from './providers/theme';
import { useRouter } from 'next/navigation';

type QuizHeaderProps = {
  /** Form impression id for the back event */
  formImpressionId?: string;
  /** user_navigated id for the cancel event */
  navigationId?: string;
};

const QuizHeader: React.FC<QuizHeaderProps> = ({
  formImpressionId,
  navigationId,
}) => {
  const trackEvent = useCallbackEvent({
    navigationId,
  });

  const dispatch = useAppDispatch();
  const experiments = useAppSelector(getExperiments);
  const inputs = useAppSelector(getInputs);
  const pageNum = useAppSelector(getPageNumber);
  const screenName = useAppSelector(getCurrentStep);
  const initialScreen = useAppSelector(getInitialScreen);
  const router = useRouter();
  const trackPageAction = useSegmentPageAction();

  const onCancel = useCallback(async () => {
    const payload = {
      form_name: FormName.Flow,
      page_number: pageNum,
    };
    try {
      trackPageAction({
        actionName: PageActionName.CcCuratedOffersCancel,
        actionType: PageActionType.Select,
        actionInput: null,
      });
    } catch (error) {
      console.error('Error in cancel track event', error);
    }
    try {
      await trackEvent(EventType.FormCancelled, payload);
    } catch (error) {
      console.error('Error in cancel track event', error);
    }

    window.location.pathname = `${getExitModalRedirectLink(
      inputs.primaryGoal as PrimaryGoal,
      inputs.collegeStudent as boolean
    )}`;
  }, [
    pageNum,
    inputs.primaryGoal,
    inputs.collegeStudent,
    trackPageAction,
    trackEvent,
  ]);

  const currentFlow = dispatch(getActiveFlow());

  const screen = currentFlow[screenName];

  const trackBackEvent = useCallbackEvent({
    navigationId,
    impressionId: formImpressionId,
  });

  const onBack = useCallback(() => {
    const payload = {
      interaction_id: uuid(),
      entity_name: EntityName.BackButtonClick,
      action: 'select',
      interaction_type: 'press',
    } as const;
    try {
      trackPageAction({
        actionName: PageActionName.CcCuratedOffersBack,
        actionType: PageActionType.Select,
        actionInput: null,
      });
    } catch (error) {
      console.error('Error in cancel track event', error);
    }
    trackBackEvent(EventType.ElementInteracted, payload);
    router.back();
  }, [router, trackBackEvent, trackPageAction]);

  const isInitialScreen = screenName === initialScreen;
  const isLoadingScreen = screenName === Screen.Loading;

  const showProgressBarV2 =
    (isNerdyTipAndProgressBarActive(experiments) ||
      isProgressBarOnlyActive(experiments)) &&
    screen?.progressRate;

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <>
      {screen?.progressRate && (
        <Box
          paddingTop={showProgressBarV2 ? 0 : 4}
          bgcolor={(theme) => theme.palette.common.white}
          sx={(theme) => ({
            [theme.breakpoints.down('sm')]: {
              zIndex: 901,
              position: 'absolute',
              top: 0,
              paddingTop: 3,
              width: '100%',
            },
          })}
        >
          {showProgressBarV2 && !isMobile && (
            <ProgressBarV2 percentage={screen?.progressRate} />
          )}

          {(!isInitialScreen || !isLoadingScreen) && (
            <Box
              paddingTop={{ xs: 0, md: showProgressBarV2 ? 4 : 0 }}
              paddingBottom={{ xs: 0, md: showProgressBarV2 ? 3 : 0 }}
              paddingX={{ xs: 0, md: 4 }}
              maxWidth={1280}
              marginX="auto"
            >
              <NavbarHeader
                showBack={!isInitialScreen && !isLoadingScreen}
                showClose={!isLoadingScreen}
                onBack={onBack}
                onCancel={onCancel}
              />
            </Box>
          )}

          {showProgressBarV2 && isMobile && (
            <ProgressBarV2 percentage={screen?.progressRate} />
          )}

          {!showProgressBarV2 && (
            <Box
              paddingX={{ xs: 3, md: 4 }}
              paddingTop={{ xs: 4, md: 5 }}
              maxWidth={1280}
              marginX="auto"
            >
              <ProgressBar
                {...getProgressValues(
                  screen?.progress !== undefined &&
                    typeof screen?.progress === 'function'
                    ? screen?.progress(inputs)
                    : screen?.progress,
                  screenName,
                  inputs.primaryGoal as PrimaryGoal
                )}
              />
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

export default QuizHeader;
